import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import DialogList from 'src/dialogs';
import S3FileUpload from 'src/models/leasing/S3FileUpload';
import { useFetch } from 'use-http';
import { PDFDocument } from 'pdf-lib';
import moment from 'moment';
import * as Constants from 'src/components/common/Constants';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import actions from 'src/actions';
import LandContractType from 'src/models/LandContractType';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import { useForm } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable, DraggingStyle, NotDraggingStyle, DropResult } from 'react-beautiful-dnd';
import PortalAwareItem from '../TractComponents/PortalAwareItem';
import { AnyObject } from 'final-form';
import FileDownloadUtil from 'src/util/FileDownloadUtil';

interface ILeasePacketBuilderProvidedProps {
    fileData: Array<S3FileUpload>;
    landType: LandContractType;
    contractLookupId: number;
    loadData: () => void;
}

type LeasePacketBuilderProps = ILeasePacketBuilderProvidedProps;

const LeasePacketBuilder = (props: LeasePacketBuilderProps) => {
    const { get, post, response } = useFetch();
    const { getValues, register } = useForm();
    const dispatch = useDispatch();
    const appContext = useSelector((state: IRootState) => state.appContext);
    const portal: HTMLElement = document.createElement('div');
    const grid = 8;
    
    const [contractPdfs, setContractPdfs] = useState<S3FileUpload[]>([]);
    const [leaseholdPdfs, setLeaseholdPdfs] = useState<S3FileUpload[]>([]);
    const [leaseTractPdfs, setLeaseTractPdfs] = useState<S3FileUpload[]>([]);
    const [docList, setDocList] = useState<S3FileUpload[]>([]);
    const [blobList, setBlobList] = useState<Blob[]>(undefined);
    const [fileTooLarge, setFileTooLarge] = useState<boolean>(false);
    const [file404, setFile404] = useState<Array<string>>([]);

    portal.classList.add('horizon-sequencing-portal');
    if (!document.body) {
        throw new Error('body not ready for portal creation!');
    }
    document.body.appendChild(portal);

    useEffect(() => {
        if (props?.fileData?.length > 0) {
            let pdfs = props.fileData.filter(fd => fd.fileExtension === 'pdf');
            let cPdfs = [];
            let lhPdfs = [];
            let ltPdfs = [];
            for (let file of pdfs) {
                if (file.sourceIdentifier.includes('Leasehold') || file.sourceIdentifier.includes('Contract Hold')) {
                    lhPdfs.push(file);
                } else if (file.sourceIdentifier.includes('Lease')) {
                    cPdfs.push(file);
                } else if (file.sourceIdentifier.includes('Horizon') || file.sourceIdentifier.includes('Surface Tract')) {
                    ltPdfs.push(file);
                } else {
                    console.log('Pdf File unaccounted for:', file);
                }
            }

            setContractPdfs(cPdfs);
            setLeaseholdPdfs(lhPdfs);
            setLeaseTractPdfs(ltPdfs);
        }
    }, [props]);

    useEffect(() => {
        if (!!blobList && blobList.length < docList.length) {
            downloadFile(docList[blobList.length].s3KeyName);
        } else if (!!blobList) {
            combinePdf();
        }
    }, [blobList]);

    const includeDocument = (index: number, file: S3FileUpload) => {
        let ref = getValues();
        let files = [...docList];
        if (ref.docs?.[index]?.include === true) {
            files.push(file);
        } else {
            let fileToRemove = docList.find(dl => dl.id === file.id);
            if (!!fileToRemove) {
                files.splice(files.indexOf(fileToRemove), 1);
            }
        } 
        setDocList(files);
    };

    const createLeasePacket = () => {
        setFileTooLarge(false);
        if (docList?.length > 0) {
            setBlobList([]);
        }
    };

    const downloadFile = async (keyName: string) => {
        await get(`/v1.0/s3bucket/${keyName}`);
        if (response.ok) {
            await response.blob().then((blob) => {
                let currentList = [...blobList];
                currentList.push(blob);
                setBlobList(currentList);
            });
        } else {
            console.log('File does not exist:', keyName);
            setFile404([...file404, keyName]);
            setBlobList([...blobList, null]);
        }
    };

    const combinePdf = async () => {
        try {
            const mergedPdf = await PDFDocument.create();

            for (let blob of blobList) {
                if (blob !== null) {
                    const arrayBuffer = await FileDownloadUtil.readBlobAsArrayBuffer(blob);
                    const existingPdf = await PDFDocument.load(arrayBuffer);
                    const copiedPages = await mergedPdf.copyPages(existingPdf, existingPdf.getPageIndices());
                    copiedPages.forEach((page) => mergedPdf.addPage(page));
                }
            }

            const mergedPdfBytes = await mergedPdf.save();
            const mergedBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
            handlePacketUpload(mergedBlob);
        } catch (error) {
            console.log('Error merging pdfs!', error);
        }
    };

    const handlePacketUpload = (mergedBlob: Blob) => {
        if (mergedBlob) {
            let today = moment(new Date()).format('MM-DD-YYYY').toString();
            let fileName = 'leasePacket_' + today + '.pdf';
            const file = new File([mergedBlob], fileName, { type: mergedBlob.type });
            const formData = new FormData();
            formData.append('fileObject', file);
            formData.append('fileName', file.name);
            formData.append('fileType', file.name.split('.').pop());
            
            if (file.size > Constants.MaxUploadFileSizeBytes) {
                setFileTooLarge(true);
            } else {
                uploadLeasePacket(formData);
            }
        }
    };

    const uploadLeasePacket = async (formData: FormData) => {
        let filePath: string;
        if (props.landType === LandContractType.OilAndGas) {
            filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/s3bucket/agreements/lease/${appContext.leaseId}`;
        } else {
            filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/contract-holds/${props.contractLookupId}/s3bucket`;
        }
        await post(
            filePath,
            formData
        );

        if (response.ok) {
            setDocList([]);
            if (file404.length > 0) {
                window.alert(`Some files could not be found: ${file404.join(', ')}`);
                setFile404([]);
            }
            props.loadData();
            dispatch(actions.layout.hideDialog());
        }
    };

    const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle, item: AnyObject) => {
        if (!isDragging) {
            draggableStyle = { ...draggableStyle, transform: null };
        }

        return {
            padding: grid * 2,
            margin: `${grid}px 0 ${grid}px 0`,
            background: isDragging ? '#c8d9e9' : item.id === 0 ? '#ffffc7' : '#f1f1f1',
            border: '2px solid #6393C1',
            ...draggableStyle,
        };
    };

    const getListStyle = (isDraggingOver: boolean) => ({
        background: isDraggingOver ? 'lightblue' : 'lightgrey',
        border: '2px dashed #6393C1',
        padding: grid,
    });

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const items = reorder(docList, result.source.index, result.destination.index);
        setDocList(items);
    };

    const reorder = (list: Array<S3FileUpload>, startIndex: number, endIndex: number) => {
        let result = [...list];

        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const customDocumentFormatting = (doc: S3FileUpload) => {
        return `${doc.sourceIdentifier} - ${doc.originalFileName}`;
    };

    const handleCloseDialog = () => {
        setDocList([]);
        setBlobList(undefined);
        setFileTooLarge(false);
        setFile404([]);
        dispatch(actions.layout.hideDialog());
    };

    return (
        <Dialog title="Lease Packet Builder" dialogName={DialogList.LEASE_PACKET_BUILDER} onCloseDialog={handleCloseDialog}>
            <div style={{ width: '75vw', maxHeight: '60vh', overflow: 'scroll' }}>
                {contractPdfs.length > 0 || leaseholdPdfs.length > 0 || leaseTractPdfs.length > 0 ? (
                    <>
                        <div>
                            {contractPdfs.length > 0 && (
                                <>
                                    <RHFLegend title="Contract Documents" />
                                    {/* <span>File Name</span> */}
                                    {contractPdfs.map((file, index) => {
                                        if (file.fileExtension === 'pdf') {
                                            return (
                                                <div key={index} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <RHFCheckbox
                                                        name={`docs[${index}].include`}
                                                        label=""
                                                        onClick={() => includeDocument(index, file)}
                                                        bodyStyle={{ width: '10%' }}
                                                        register={register}
                                                    />
                                                    <span>{file.originalFileName}</span>
                                                </div>
                                            );
                                        }
                                        return <div key={index} />;
                                    })}
                                </>
                            )}
                            {leaseholdPdfs.length > 0 && (
                                <>
                                    <RHFLegend title="Leasehold Documents" />
                                    {/* <span>File Name</span> */}
                                    {leaseholdPdfs.map((file, index) => {
                                        let currIndex = index + contractPdfs.length;
                                        if (file.fileExtension === 'pdf') {
                                            return (
                                                <div key={index} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <RHFCheckbox
                                                        name={`docs[${currIndex}].include`}
                                                        label=""
                                                        onClick={() => includeDocument(currIndex, file)}
                                                        bodyStyle={{ width: '10%' }}
                                                        register={register}
                                                    />
                                                    <span>{file.sourceIdentifier} - {file.originalFileName}</span>
                                                </div>
                                            );
                                        }
                                        return <div key={index} />;
                                    })}
                                </>
                            )}
                            {leaseTractPdfs.length > 0 && (
                                <>
                                    <RHFLegend title="Lease Tract Documents" />
                                    {/* <span>File Name</span> */}
                                    {leaseTractPdfs.map((file, index) => {
                                        let currIndex = index + contractPdfs.length + leaseholdPdfs.length;
                                        if (file.fileExtension === 'pdf') {
                                            return (
                                                <div key={index} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <RHFCheckbox
                                                        name={`docs[${currIndex}].include`}
                                                        label=""
                                                        onClick={() => includeDocument(currIndex, file)}
                                                        bodyStyle={{ width: '10%' }}
                                                        register={register}
                                                    />
                                                    <span>{file.sourceIdentifier} - {file.originalFileName}</span>
                                                </div>
                                            );
                                        }
                                        return <div key={index} />;
                                    })}
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <div>No PDF Files Uploaded</div>
                )}
                <div className="dlc-form-error" hidden={!fileTooLarge}>Lease Packet is too large!</div>
                {docList.length > 0 && (
                    <>
                        <RHFLegend title="Sequence Selected Documents." />
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="sequence">
                                {(provided, snapshot) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                                        {docList.map((item: S3FileUpload, index: number) => {
                                            return (
                                                <Draggable key={`item-${item.id}`} draggableId={`item-${item.id}`} index={index}>
                                                    {(dragProvided, dragSnapshot) => (
                                                        <div
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                        >
                                                            <PortalAwareItem
                                                                item={item}
                                                                provided={dragProvided}
                                                                snapshot={dragSnapshot}
                                                                getItemStyle={(isDragging, draggableStyle) =>
                                                                    getItemStyle(isDragging, draggableStyle, item)
                                                                }
                                                                customDetailFormat={customDocumentFormatting}
                                                                portal={portal}
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </>
                )}
            </div>
            <Button
                text={'Generate Lease Packet'}
                size="medium"
                onClick={createLeasePacket}
                disabled={!docList || docList.length === 0}
            />
        </Dialog>
    );
};

export default LeasePacketBuilder;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/LeasePacketBuilder.tsx